import { DEAL_TYPE_ID, INVOICE_TYPE_ID, QUOTE_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useMemo } from 'react';
import { Scopes } from '../../../utils/constants';
import { hasScope } from '../../../utils/auth';
import { handleError } from '../../../utils/errorHandling';
import { getLineItemConversionValidation } from '../getLineItemConversionValidation';
import { ObjectConversionError } from '../types/validation';
import { useFetchConvertableObjectInfo } from './useFetchConvertableObjectInfo';
/**
 * Validates the object is supported for the billing wizard.
 * @param objectTypeId - Object Type from customer-data-objects/constants/ObjectTypeIds
 * @param objectId - The Id of the object to validate.
 * @returns {
 *   objectConversionErrors: Set<ObjectConversionError>,
 *   lineItemConversionErrors: Set<ObjectConversionError>
 * }
 */
export function useValidateObjectForConversion({
  objectTypeId,
  objectId
}) {
  const hasCreateAccess = hasScope(Scopes.subscription.write);
  const {
    data,
    error,
    loading,
    refetch
  } = useFetchConvertableObjectInfo(objectTypeId, objectId);
  return useMemo(() => {
    const result = {
      objectConversionErrors: new Set(),
      lineItemConversionErrors: new Set(),
      loading
    };
    if (!objectTypeId) {
      result.objectConversionErrors.add(ObjectConversionError.NO_OBJECT_TYPE);
      return result;
    }
    if (!objectId) {
      result.objectConversionErrors.add(ObjectConversionError.NO_OBJECT_ID);
      return result;
    }
    switch (objectTypeId) {
      case DEAL_TYPE_ID:
      case QUOTE_TYPE_ID:
      case INVOICE_TYPE_ID:
        break;
      default:
        result.objectConversionErrors.add(ObjectConversionError.UNSUPPORTED_OBJECT_TYPE);
        break;
    }
    if (loading) {
      result.objectConversionErrors.add(ObjectConversionError.STILL_LOADING);
      return result;
    }
    if (error) {
      result.objectConversionErrors.add(ObjectConversionError.UNKNOWN_ERROR);
      return result;
    }
    if (!hasCreateAccess) {
      result.objectConversionErrors.add(ObjectConversionError.NO_CREATE_ACCESS);
      return result;
    }
    if (!data) {
      refetch().catch(handleError);
      result.objectConversionErrors.add(ObjectConversionError.OBJECT_NOT_FOUND);
      return result;
    }
    getLineItemConversionValidation({
      subject: data
    }).forEach(err => {
      result.lineItemConversionErrors.add(err);
    });
    return result;
  }, [data, error, hasCreateAccess, loading, objectId, objectTypeId, refetch]);
}